import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  RotateCcw,
  Eye,
  File,
  Paperclip,
  SendHorizontal,
  X,
  Loader2,
  ArrowLeftIcon,
} from 'lucide-react';
import {
  TicketDetailsWrapper,
  TicketDetailsContainer,
  PanelCard,
  PanelHeader,
  HeaderTopRow,
  MetaRow,
  StatusBadge,
  StatusDot,
  CategoryBadge,
  MessagesContainer,
  MessageGroup,
  MessageMeta,
  AuthorAvatar,
  AuthorLabel,
  TimeText,
  MessageBubble,
  MessageText,
  AttachmentsRow,
  AttachmentPill,
  AttachmentName,
  AttachmentSize,
  ComposerWrapper,
  PendingAttachmentsRow,
  PendingAttachmentPill,
  PendingAttachmentActions,
  IconButton,
  ComposerBox,
  ReplyTextarea,
  ComposerActionsBar,
  AttachButton,
  HiddenFileInput,
  SendButton,
  HelperText,
  SpinWrapper,
  PageHeader,
  HeaderLeft,
  TitleRow,
  Title,
  TicketTitle,
  MobileBackButton,
} from './ticketDetails.styles';
import { EmptyState, OutlineButon, RetryButton } from '../../../settings/appStyle/global.style';
import { useClientQueryList, useClientCreateQuery } from './hooks/useTicketQueries';
import {
  useUploadAttachment,
  useViewAttachment,
  useRemoveAttachment,
  isAllowedAttachment,
} from './hooks/useTicketAttachments';
import { formatDateTime } from '../../../helper/dateRelated';
import {
  HeaderActions,
  RefreshIconButton,
} from '../../AdminPages/AdminTicketDetails/adminTicketDetails.styles';
import { renderMultiline } from '../../../helper/renderMultiline';

const MAX_TEXTAREA_LINES = 3;
const PAGE_SIZE = 10;
const MAX_ATTACHMENTS = 5;
const SCROLL_TOP_THRESHOLD = 60;
const NEAR_BOTTOM_THRESHOLD = 120;

const formatBytes = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

let attachmentIdCounter = 0;
const nextLocalAttachmentId = () => {
  attachmentIdCounter += 1;
  return `pending-${Date.now()}-${attachmentIdCounter}`;
};

const getCreatedTime = (q) => {
  const t = new Date(q?.createdAt).getTime();
  return Number.isNaN(t) ? 0 : t;
};

// Dedupe by _id (incoming wins), then sort oldest -> newest by createdAt.
const mergeQueries = (existing, incoming) => {
  const map = new Map();
  existing.forEach((q) => map.set(q._id, q));
  incoming.forEach((q) => map.set(q._id, q));
  return Array.from(map.values()).sort((a, b) => {
    const diff = getCreatedTime(a) - getCreatedTime(b);
    if (diff !== 0) return diff;
    return String(a._id).localeCompare(String(b._id));
  });
};

const TicketDetails = () => {
  const { ticketId } = useParams();

  const [replyText, setReplyText] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [viewingAttachmentId, setViewingAttachmentId] = useState(null);

  const [page, setPage] = useState(1);
  const [allQueries, setAllQueries] = useState([]);
  const [ticketMeta, setTicketMeta] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalQueries, setTotalQueries] = useState(null);
  const [reachedEnd, setReachedEnd] = useState(false);

  const hasMore =
    !reachedEnd && (typeof totalQueries === 'number' ? allQueries.length < totalQueries : true);

  const mergedPagesRef = useRef(new Set());
  const prevScrollHeightRef = useRef(0);
  const prevScrollTopRef = useRef(0);
  const scrollModeRef = useRef(null); // 'bottom' | 'prepend' | null
  const isNearBottomRef = useRef(true);
  const shouldScrollToBottomRef = useRef(true);

  const fileInputRef = useRef(null);
  const threadEndRef = useRef(null);
  const textareaRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const { data, isLoading, isError, refetch, isFetching } = useClientQueryList(ticketId, {
    page,
    limit: PAGE_SIZE,
  });

  const uploadAttachmentMutation = useUploadAttachment();
  const viewAttachmentMutation = useViewAttachment();
  const removeAttachmentMutation = useRemoveAttachment();
  const createQueryMutation = useClientCreateQuery({
    onSuccess: () => {
      setReplyText('');
      setPendingAttachments([]);
      shouldScrollToBottomRef.current = true;
      mergedPagesRef.current = new Set();
      if (page !== 1) {
        setPage(1);
      }
    },
  });

  useEffect(() => {
    if (!data) return;

    // Ignore stale data (e.g. previous page's data while the next one is loading)
    const responsePage = Number(data.page) || page;
    if (responsePage !== page) return;

    if (data.ticketData) setTicketMeta(data.ticketData);

    const total = data.totalQueries ?? data.totalCount;
    if (typeof total === 'number') setTotalQueries(total);

    const fetched = data.queries ?? [];
    const el = messagesContainerRef.current;

    if (page === 1) {
      // Initial load / refresh / new message: only go to bottom if requested
      // or the user is already near the bottom. Otherwise leave their position alone.
      if (shouldScrollToBottomRef.current || isNearBottomRef.current) {
        scrollModeRef.current = 'bottom';
      }
      shouldScrollToBottomRef.current = false;
      mergedPagesRef.current.add(1);
      setReachedEnd(fetched.length < PAGE_SIZE);
    } else {
      if (mergedPagesRef.current.has(page)) return;
      mergedPagesRef.current.add(page);

      // Older messages are being prepended: remember scroll metrics to restore position
      scrollModeRef.current = 'prepend';
      prevScrollHeightRef.current = el?.scrollHeight ?? 0;
      prevScrollTopRef.current = el?.scrollTop ?? 0;
      setReachedEnd(fetched.length < PAGE_SIZE);
    }

    setAllQueries((prev) => mergeQueries(prev, fetched));
    setIsLoadingMore(false);
  }, [data, page]);

  const queries = allQueries;
  const latestQuery = queries[queries.length - 1];

  const latestUserQuery = [...queries].reverse().find((q) => q.userType === 'USER');
  const queryId = latestUserQuery?._id;

  const folderName = ticketMeta?.folderName;
  const ticketCategory = ticketMeta?.ticketType || '--';
  const title = ticketMeta?.title;
  const clientId = latestQuery?.clientId || data?.ticket?.clientId || '';
  const ticketStatus = (ticketMeta?.status || 'OPEN').toUpperCase();
  const isTicketOpen = ticketStatus === 'OPEN';

  const messages = useMemo(
    () =>
      queries.map((q) => ({
        id: q._id,
        isQuery: q.userType === 'USER',
        author: q.userType === 'USER' ? 'You' : 'Support',
        initials: q.userType === 'USER' ? 'YOU' : 'SP',
        time: formatDateTime(q.createdAt),
        content: q.query || '',
        attachments: (q.documentsArray || []).map((doc) => ({
          id: doc.documentId,
          name: doc.name || 'Attachment',
          size: formatBytes(doc.fileSize),
          queryId: q._id,
        })),
      })),
    [queries]
  );

  useLayoutEffect(() => {
    const mode = scrollModeRef.current;
    if (!mode) return;
    scrollModeRef.current = null;

    const el = messagesContainerRef.current;
    if (!el) return;

    if (mode === 'prepend') {
      // Keep the same message under the viewport after older ones are inserted above
      el.scrollTop = prevScrollTopRef.current + (el.scrollHeight - prevScrollHeightRef.current);
      return;
    }

    const scrollToBottom = () => {
      el.scrollTop = el.scrollHeight;
    };
    scrollToBottom();
    requestAnimationFrame(() => {
      scrollToBottom();
      requestAnimationFrame(scrollToBottom);
    });
  }, [allQueries]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    el.style.height = 'auto';
    const computed = window.getComputedStyle(el);
    const lineHeight = parseFloat(computed.lineHeight) || 20;
    const paddingTop = parseFloat(computed.paddingTop) || 0;
    const paddingBottom = parseFloat(computed.paddingBottom) || 0;
    const maxHeight = lineHeight * MAX_TEXTAREA_LINES + paddingTop + paddingBottom;

    const nextHeight = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [replyText]);

  const handleBack = () => window.history.back();

  const handleAttachClick = () => fileInputRef.current?.click();

  const uploadFilesSequentially = useCallback(
    async (queueItems) => {
      for (const { id, file } of queueItems) {
        setPendingAttachments((prev) =>
          prev.map((att) => (att.id === id ? { ...att, status: 'uploading' } : att))
        );

        try {
          // eslint-disable-next-line no-await-in-loop
          const result = await uploadAttachmentMutation.mutateAsync({
            file,
            folderName: folderName || 'unassigned-client',
            rootFolder: 'ticket_docs',
          });

          setPendingAttachments((prev) =>
            prev.map((att) =>
              att.id === id
                ? {
                    ...att,
                    status: 'done',
                    documentId: result?.documentId,
                    fileSize: result?.fileSize ?? file.size,
                    applicationType: result?.applicationType ?? file.type,
                  }
                : att
            )
          );
        } catch (err) {
          setPendingAttachments((prev) => prev.filter((att) => att.id !== id));
          window.alert(
            `Failed to upload "${file.name}"${
              err?.message ? `: ${err.message}` : '. Please try again.'
            }`
          );
        }
      }
    },
    [uploadAttachmentMutation, folderName]
  );

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const nonImage = files.filter((f) => !isAllowedAttachment(f));
    if (nonImage.length) {
      window.alert('Only image and PDF files can be attached right now.');
    }
    let imageFiles = files.filter(isAllowedAttachment);
    if (!imageFiles.length) return;

    const existingCount = pendingAttachments.length;
    const remainingSlots = MAX_ATTACHMENTS - existingCount;

    if (remainingSlots <= 0) {
      window.alert(`You can attach up to ${MAX_ATTACHMENTS} images per reply.`);
      return;
    }

    if (imageFiles.length > remainingSlots) {
      window.alert(
        `You can attach up to ${MAX_ATTACHMENTS} images per reply. Only the first ${remainingSlots} of the selected images will be uploaded.`
      );
      imageFiles = imageFiles.slice(0, remainingSlots);
    }

    const queueItems = imageFiles.map((file, index) => ({
      id: nextLocalAttachmentId(),
      file,
      status: index === 0 ? 'uploading' : 'queued',
    }));

    setPendingAttachments((prev) => [
      ...prev,
      ...queueItems.map(({ id, file, status }) => ({
        id,
        name: file.name,
        size: formatBytes(file.size),
        status,
      })),
    ]);

    uploadFilesSequentially(queueItems);
  };

  const handleRemovePendingAttachment = (attachment) => {
    if (attachment.status !== 'done' || !attachment.documentId) {
      setPendingAttachments((prev) => prev.filter((att) => att.id !== attachment.id));
      return;
    }

    setPendingAttachments((prev) =>
      prev.map((att) => (att.id === attachment.id ? { ...att, removing: true } : att))
    );

    removeAttachmentMutation.mutate(
      { documentId: attachment.documentId, ticketId, queryId },
      {
        onSuccess: () => {
          setPendingAttachments((prev) => prev.filter((att) => att.id !== attachment.id));
        },
        onError: (err) => {
          setPendingAttachments((prev) =>
            prev.map((att) => (att.id === attachment.id ? { ...att, removing: false } : att))
          );
          window.alert(
            `Failed to remove "${attachment.name}"${
              err?.message ? `: ${err.message}` : '. Please try again.'
            }`
          );
        },
      }
    );
  };

  const handleRefresh = async () => {
    shouldScrollToBottomRef.current = true;
    mergedPagesRef.current = new Set();
    if (page !== 1) {
      setPage(1);
    } else {
      await refetch();
    }
  };

  // Infinite scroll: once the user scrolls near the top of the thread,
  // load the next (older) page.
  const handleThreadScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;

    isNearBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_THRESHOLD;

    if (el.scrollTop > SCROLL_TOP_THRESHOLD) return;
    if (!allQueries.length || isLoadingMore || isFetching || !hasMore) return;

    setIsLoadingMore(true);
    setPage((p) => p + 1);
  }, [allQueries.length, isLoadingMore, isFetching, hasMore]);

  const handleViewAttachment = (documentId) => {
    if (!documentId || viewingAttachmentId) return;
    setViewingAttachmentId(documentId);

    viewAttachmentMutation.mutate(
      { documentId, ticketId },
      {
        onError: (err) => {
          window.alert(
            `Failed to open attachment${err?.message ? `: ${err.message}` : '. Please try again.'}`
          );
        },
        onSettled: () => setViewingAttachmentId(null),
      }
    );
  };

  const hasUploadingAttachments = pendingAttachments.some(
    (att) => att.status === 'uploading' || att.status === 'queued'
  );
  const hasBusyAttachments = pendingAttachments.some((att) => att.removing);
  const attachmentLimitReached = pendingAttachments.length >= MAX_ATTACHMENTS;

  const handleSendReply = () => {
    const readyAttachments = pendingAttachments.filter((att) => att.status === 'done');
    if (!replyText.trim() && readyAttachments.length === 0) return;
    if (hasUploadingAttachments || hasBusyAttachments) return;

    shouldScrollToBottomRef.current = true;
    createQueryMutation.mutate({
      clientId,
      ticketId,
      query: replyText.trim(),
      documentsArray: readyAttachments.map((att) => ({
        name: att.name,
        documentId: att.documentId,
        fileSize: att.fileSize,
        applicationType: att.applicationType,
      })),
    });
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSendReply();
    }
  };

  const renderThread = () => {
    if (isLoading && page === 1 && allQueries.length === 0) {
      return (
        <EmptyState>
          <SpinWrapper>
            <Loader2 style={{ width: '20px', height: '20px' }} />
          </SpinWrapper>
          <p>Loading conversation…</p>
        </EmptyState>
      );
    }

    if (isError && allQueries.length === 0) {
      return (
        <EmptyState>
          <p>Failed to load conversation</p>
          <p>Please try again later</p>
          <RetryButton type="button" onClick={() => refetch()} disabled={isFetching}>
            <RotateCcw />
            {isFetching ? 'Retrying…' : 'Retry'}
          </RetryButton>
        </EmptyState>
      );
    }

    if (messages.length === 0) {
      return (
        <EmptyState>
          <p>No messages yet</p>
          <p>Send a reply to start the conversation</p>
        </EmptyState>
      );
    }

    return (
      <>
        {isLoadingMore && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0', gap: '3px' }}>
            <SpinWrapper>
              <Loader2 style={{ width: '16px', height: '16px' }} />
            </SpinWrapper>{' '}
            <p>Loading more conversations…</p>
          </div>
        )}
        {messages.map((message) => (
          <MessageGroup key={message.id} $isQuery={message.isQuery}>
            <MessageMeta $isQuery={message.isQuery}>
              {!message.isQuery && <AuthorAvatar>{message.initials}</AuthorAvatar>}
              <AuthorLabel>
                {message.isQuery ? 'QUERY' : 'RESPONSE'} • {message.author}
              </AuthorLabel>
              <TimeText>{message.time}</TimeText>
            </MessageMeta>

            <MessageBubble $isQuery={message.isQuery}>
              <MessageText $isQuery={message.isQuery}>
                {renderMultiline(message.content)}
              </MessageText>

              {message.attachments.length > 0 && (
                <AttachmentsRow $isQuery={message.isQuery}>
                  {message.attachments.map((att) => {
                    const isViewing = viewingAttachmentId === att.id;
                    return (
                      <AttachmentPill
                        key={att.id}
                        type="button"
                        $isQuery={message.isQuery}
                        onClick={() => handleViewAttachment(att.id)}
                        disabled={isViewing}
                        title="View attachment"
                      >
                        {isViewing ? (
                          <SpinWrapper>
                            <Loader2 />
                          </SpinWrapper>
                        ) : (
                          <File size={12} />
                        )}
                        <AttachmentName>{att.name}</AttachmentName>
                        <AttachmentSize>({att.size})</AttachmentSize>
                      </AttachmentPill>
                    );
                  })}
                </AttachmentsRow>
              )}
            </MessageBubble>
          </MessageGroup>
        ))}
        <div ref={threadEndRef} />
      </>
    );
  };

  return (
    <TicketDetailsWrapper>
      <TicketDetailsContainer>
        <PageHeader>
          <HeaderLeft>
            <TitleRow>
              <Title>Ticket Details</Title>
            </TitleRow>
          </HeaderLeft>

          <OutlineButon onClick={handleBack} type="button">
            Back
          </OutlineButon>
        </PageHeader>

        <PanelCard>
          <PanelHeader>
            <HeaderTopRow>
              <div>
                <MetaRow>
                  <TicketTitle>{title}</TicketTitle>
                  <CategoryBadge>{ticketCategory}</CategoryBadge>
                  <StatusBadge $status={ticketStatus}>
                    <StatusDot />
                    {ticketStatus}
                  </StatusBadge>
                </MetaRow>
              </div>

              <HeaderActions>
                <RefreshIconButton
                  type="button"
                  onClick={handleRefresh}
                  disabled={isFetching}
                  title="Refresh"
                >
                  {isFetching ? (
                    <SpinWrapper>
                      <Loader2 size={14} />
                    </SpinWrapper>
                  ) : (
                    <RotateCcw size={14} />
                  )}
                </RefreshIconButton>
                <MobileBackButton onClick={handleBack} type="button">
                  <ArrowLeftIcon />
                  Back
                </MobileBackButton>
              </HeaderActions>
            </HeaderTopRow>
          </PanelHeader>

          <MessagesContainer ref={messagesContainerRef} onScroll={handleThreadScroll}>
            {renderThread()}
          </MessagesContainer>

          {isTicketOpen && (
            <ComposerWrapper>
              {pendingAttachments.length > 0 && (
                <PendingAttachmentsRow>
                  {pendingAttachments.map((att) => {
                    const isQueued = att.status === 'queued';
                    const isUploading = att.status === 'uploading';
                    const isViewing = viewingAttachmentId === att.documentId;
                    const isBusy = isUploading || isQueued || att.removing;

                    return (
                      <PendingAttachmentPill key={att.id} $busy={isBusy}>
                        {(isUploading || isQueued) && (
                          <SpinWrapper>
                            <Loader2 />
                          </SpinWrapper>
                        )}
                        <AttachmentName>
                          {att.name}
                          {isUploading && ' (uploading…)'}
                          {isQueued && ' (queued…)'}
                          {att.removing && ' (removing…)'}
                        </AttachmentName>
                        <AttachmentSize>({att.size})</AttachmentSize>
                        <PendingAttachmentActions>
                          {att.status === 'done' && !att.removing && (
                            <IconButton
                              type="button"
                              title="View"
                              disabled={isViewing}
                              onClick={() => handleViewAttachment(att.documentId)}
                            >
                              {isViewing ? (
                                <SpinWrapper>
                                  <Loader2 />
                                </SpinWrapper>
                              ) : (
                                <Eye />
                              )}
                            </IconButton>
                          )}
                          <IconButton
                            type="button"
                            title="Remove"
                            disabled={att.removing}
                            onClick={() => handleRemovePendingAttachment(att)}
                          >
                            {att.removing ? (
                              <SpinWrapper>
                                <Loader2 />
                              </SpinWrapper>
                            ) : (
                              <X />
                            )}
                          </IconButton>
                        </PendingAttachmentActions>
                      </PendingAttachmentPill>
                    );
                  })}
                </PendingAttachmentsRow>
              )}

              <ComposerBox>
                <ReplyTextarea
                  ref={textareaRef}
                  rows={1}
                  placeholder="Write a reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <ComposerActionsBar>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <AttachButton
                      type="button"
                      onClick={handleAttachClick}
                      disabled={attachmentLimitReached}
                      title={
                        attachmentLimitReached
                          ? `Max ${MAX_ATTACHMENTS} images per reply`
                          : undefined
                      }
                    >
                      <Paperclip />
                      Attach images/PDFs
                    </AttachButton>

                    <HelperText>
                      (Our team will be notified automatically when you reply. Max {MAX_ATTACHMENTS}{' '}
                      images.)
                    </HelperText>
                  </div>
                  <HiddenFileInput
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf,.pdf"
                    multiple
                    onChange={handleFilesSelected}
                  />
                  <SendButton
                    type="button"
                    onClick={handleSendReply}
                    disabled={
                      createQueryMutation.isPending || hasUploadingAttachments || hasBusyAttachments
                    }
                  >
                    {createQueryMutation.isPending ? 'Sending...' : 'Send'}
                    <SendHorizontal />
                  </SendButton>
                </ComposerActionsBar>
              </ComposerBox>
            </ComposerWrapper>
          )}
        </PanelCard>
      </TicketDetailsContainer>
    </TicketDetailsWrapper>
  );
};

export default TicketDetails;
